/**
 * Narrative HTML sanitizer, for every surface that renders the report narrative.
 *
 * The report narrative (report_narrative_html) is raw HTML produced by Claude's
 * write_report_narrative tool from the seller's inbound email + T-12, so a
 * prompt-injected email or document decides what it says. Signed-in users are
 * no safer from it than anonymous ones: AnalysisContent runs every narrative
 * through this before its dangerouslySetInnerHTML (the in-app panel, a
 * property's analysis tab and the public share page, for every stored row), and
 * a share snapshot is also sanitized with it when it is stored.
 *
 * Strategy: strict allowlist, in one left-to-right pass. Dangerous element
 * blocks go with their content, comments go, and every other tag is rebuilt
 * from scratch - an allowlisted tag keeps ONLY a validated href (on <a>) and
 * NOTHING else, so no on*=, style=, srcdoc, or any other attribute can survive.
 * Non-allowlisted tags are removed but their inner text is preserved. Every "<"
 * that does not open a tag is written as &lt;, so the only markup in the output
 * is the tags written here. The chain of regex passes this replaced could be
 * walked around: removing a dropped tag joined a stray "<" to the text after it
 * ("<<x>img src=x onerror=...>" came out as a live <img>), and a name with an
 * underscore ("<x_y onanimationstart=...>") failed their \b and passed through
 * whole. This is intentionally conservative: the narrative is only ever
 * paragraphs, emphasis and the occasional list/link.
 *
 * Linear in its input: it runs in the browser on every render, and the regex
 * passes took seconds on a pathological narrative at MAX_LEN.
 *
 * Pure and dependency-free so it can be unit-tested with `deno test` and vitest
 * without a DOM or network, and imported by the frontend.
 */

const ALLOWED_TAGS = new Set([
  'p', 'br', 'strong', 'b', 'em', 'i', 'u',
  'ul', 'ol', 'li', 'a', 'h3', 'h4', 'h5', 'blockquote', 'span',
]);

// Elements whose entire contents must go, not just the tags.
const DANGEROUS_BLOCKS = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'noscript', 'template', 'svg', 'math', 'form', 'input',
  'button', 'select', 'textarea', 'link', 'meta', 'base', 'title', 'head', 'body', 'html',
]);

const MAX_LEN = 100_000;

// One tag, tried only AT a "<" (sticky): an optional "/", a name read the way a
// browser reads one (up to whitespace, "/" or ">"), then everything up to the
// next ">". The "/" and the spaces after it are one optional group, so a run of
// spaces can't be split between two \s* in every possible way.
const TAG = /<\s*(?:(\/)\s*)?([a-zA-Z][^\s/>]*)([^>]*)>/y;

interface Tag {
  closing: boolean;
  /** Lowercased. */
  name: string;
  attrs: string;
  /** Index just past the tag's ">". */
  end: number;
}

export function sanitizeNarrativeHtml(input: string | null | undefined): string {
  if (!input) return '';
  const html = String(input).slice(0, MAX_LEN);
  // No tag can close after the last ">", so no "<" past it is tried as one -
  // each would otherwise scan to the end for a ">" that isn't there.
  const lastGt = html.lastIndexOf('>');
  const noCloserFrom = new Map<string, number>();

  let out = '';
  let i = 0;
  while (i < html.length) {
    const lt = html.indexOf('<', i);
    if (lt === -1) {
      out += html.slice(i);
      break;
    }
    out += html.slice(i, lt);

    // A comment goes; one left open runs to the end, as it does in a browser.
    if (html.startsWith('<!--', lt)) {
      const close = html.indexOf('-->', lt + 4);
      if (close === -1) break;
      i = close + 3;
      continue;
    }

    const tag = lt < lastGt ? matchTag(html, lt) : null;
    if (!tag) {
      out += '&lt;'; // a "<" that opens no tag is text
      i = lt + 1;
      continue;
    }
    i = tag.end;

    if (DANGEROUS_BLOCKS.has(tag.name)) {
      // The tag goes, and its content with it when the element is closed.
      if (!tag.closing) i = endOfBlock(html, tag.name, i, lastGt, noCloserFrom);
      continue;
    }
    if (!ALLOWED_TAGS.has(tag.name)) continue; // drop the tag, keep inner text
    if (tag.closing) {
      out += `</${tag.name}>`;
    } else if (tag.name === 'a') {
      const href = extractSafeHref(tag.attrs);
      out += href
        ? `<a href="${href}" target="_blank" rel="noopener noreferrer nofollow">`
        : '<a>';
    } else {
      out += `<${tag.name}>`;
    }
  }

  return out.trim();
}

function matchTag(html: string, at: number): Tag | null {
  TAG.lastIndex = at;
  const m = TAG.exec(html);
  if (!m) return null;
  return { closing: m[1] === '/', name: m[2].toLowerCase(), attrs: m[3], end: TAG.lastIndex };
}

/**
 * Where a dangerous element's content ends: just past its closing tag, or
 * `from` (so only the opening tag goes) when it has none. A search that finds
 * none is remembered, so a run of unclosed tags can't rescan the rest each time.
 */
function endOfBlock(
  html: string,
  name: string,
  from: number,
  lastGt: number,
  noCloserFrom: Map<string, number>,
): number {
  if ((noCloserFrom.get(name) ?? Infinity) <= from) return from;
  let at = from;
  for (;;) {
    const lt = html.indexOf('<', at);
    if (lt === -1 || lt > lastGt) break;
    const tag = matchTag(html, lt);
    if (!tag) {
      at = lt + 1;
      continue;
    }
    if (tag.closing && tag.name === name) return tag.end;
    at = tag.end;
  }
  noCloserFrom.set(name, from);
  return from;
}

/** True if the string contains any C0 control char (0x00-0x1F) or DEL (0x7F). */
function hasControlChar(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

/**
 * Pull the href out of a raw attribute STRING and check it with
 * safeHrefValue({ allowRelative: true }): an absolute http(s)/mailto URL or a
 * same-origin relative one (a path, query or fragment; never "//host"), with
 * any javascript:/data:/vbscript:/etc. scheme refused, entity- or
 * whitespace-obfuscated or not. The value's character references are decoded
 * once and the result escaped once for href="": it used to escape an
 * already-encoded "&amp;" a second time ("&amp;amp;"), which broke every
 * narrative link with a query string. Code that has the attribute VALUE calls
 * safeHrefValue directly.
 */
export function extractSafeHref(attrs: string): string | null {
  const m = attrs.match(/href\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'>]+))/i);
  if (!m) return null;
  return safeHrefValue(m[2] ?? m[3] ?? m[4] ?? '', { allowRelative: true });
}

function safeFromCharCode(code: number): string {
  if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return '';
  try {
    return String.fromCodePoint(code);
  } catch {
    return '';
  }
}

// The named references decoded in an href value: enough to keep a URL's structure and its scheme honest.
const HREF_NAMED_ENTITIES = new Map<string, string>([
  ['amp', '&'], ['lt', '<'], ['gt', '>'], ['quot', '"'], ['apos', "'"], ['colon', ':'], ['tab', '\t'], ['newline', '\n'],
]);

// A numeric reference resolves to U+FFFD when it names nothing usable, as in HTML.
const REPLACEMENT_CHAR = String.fromCharCode(0xfffd);

// What is written percent-encoded, never raw, whatever it arrived as (typed, or decoded from a reference): the C1
// controls, and the bidi controls (ALM, LRM, RLM, the embeddings and overrides, the isolates), one of which in a shown
// URL reorders what the reader sees. A URL has no use for any of them. Built from code points: they must not sit in
// this file as literals.
const HREF_ENCODED = new RegExp(
  '[' + [[0x80, 0x9f], [0x61c, 0x61c], [0x200e, 0x200f], [0x202a, 0x202e], [0x2066, 0x2069]]
    .map(([a, b]) => `${String.fromCharCode(a)}-${String.fromCharCode(b)}`).join('') + ']',
  'g',
);

/** Decodes the character references of an attribute value, once (an &amp; is decoded, what it leaves is not). */
function decodeHrefEntities(value: string): string {
  if (!value.includes('&')) return value;
  return value.replace(
    /&(?:#[xX]([0-9a-fA-F]+);?|#([0-9]+);?|([A-Za-z]+);)/g,
    (match, hex: string | undefined, dec: string | undefined, name: string | undefined) => {
      if (name !== undefined) return HREF_NAMED_ENTITIES.get(name.toLowerCase()) ?? match;
      const code = hex !== undefined ? parseInt(hex, 16) : parseInt(dec as string, 10);
      if (code === 0 || (code >= 0xd800 && code <= 0xdfff)) return REPLACEMENT_CHAR;
      return safeFromCharCode(code) || REPLACEMENT_CHAR;
    },
  );
}

export interface SafeHrefOptions {
  /**
   * Also accept a same-origin relative URL: a path, query or fragment. Never a network path ("//host", "\\host"
   * and their mixes, which leave the origin) and never anything with a scheme, however it is spaced. For HTML the
   * app renders (the narrative); mail has no base to resolve a relative URL against, so email code leaves it off.
   */
  allowRelative?: boolean;
}

/** A relative URL that stays on the page's origin, as allowRelative accepts it. `url` is already trimmed. */
function isOriginRelative(url: string): boolean {
  if (!url) return false;
  // Collapsing every space first is stricter than a URL parser (which reads "java script:" as a path), on purpose.
  if (/^[a-z][a-z0-9+.-]*:/i.test(url.replace(/\s+/g, ''))) return false;
  return !/^[/\\]{2}/.test(url);
}

/**
 * The value-level href check. `raw` is the text of ONE href attribute value as the sender wrote it, entities and
 * all (not an attribute string: see extractSafeHref for that). In a browser's order:
 *   1. decode the character references, once;
 *   2. do what a URL parser does before it reads a URL: trim C0 controls and spaces from both ends and drop tab and
 *      newline wherever they are;
 *   3. refuse a control character, and anything that is not an absolute http(s) or mailto URL. That includes
 *      relative and protocol-relative URLs, unless `allowRelative` admits a relative one (never "//host");
 *   4. percent-encode the C1 controls and the bidi controls, whether typed or decoded from a reference (a numeric
 *      reference to 0x80-0x9F is read as that code point, not as the Windows-1252 character a browser would show);
 *   5. escape the result once for a double-quoted attribute: & as &amp;, and a quote or angle bracket
 *      percent-encoded.
 * Returns that value, or null. The result has no quoting problem whatever mix of quotes the URL holds, nothing in
 * it is escaped twice ("&quot;" in the source arrives as %22, not as "&amp;quot;"), and nothing invisible is left in
 * it, so the value is as safe to show as it is to write.
 */
export function safeHrefValue(raw: string, opts: SafeHrefOptions = {}): string | null {
  const decoded = decodeHrefEntities(raw);
  let start = 0;
  let end = decoded.length;
  while (start < end && decoded.charCodeAt(start) <= 0x20) start++;
  while (end > start && decoded.charCodeAt(end - 1) <= 0x20) end--;
  const url = decoded.slice(start, end).replace(/[\t\n\r]/g, '');
  if (hasControlChar(url)) return null;
  if (!/^(?:https?:|mailto:)/i.test(url) && !(opts.allowRelative && isOriginRelative(url))) return null;
  return url
    .replace(HREF_ENCODED, (c) => encodeURIComponent(c))
    .replace(/&/g, '&amp;')
    .replace(/"/g, '%22')
    .replace(/</g, '%3C')
    .replace(/>/g, '%3E');
}
